/* ------------------------------------------------------------------ */
/*  product-page.js — v3 (About-aware, bug-fix version)               */
/*    – Sticky meta bar that mirrors each product section             */
/*    – Dynamic size filtering & variant selection                    */
/*    – Hash updates (#handle / #about) during scroll                 */
/*    – Hides all .meta-data + sticky bar when “About” in view        */
/*    – Cart drawer with live preview                                 */
/* ------------------------------------------------------------------ */
/* global Shopify */

document.addEventListener('DOMContentLoaded', () => {
  'use strict';

  /* ───────────────────────────────
     GLOBAL STATE & DOM HOOKS
  ─────────────────────────────── */
  let activeVariantId = null;
  let currentSection  = null;
  let inAbout         = false;                       // ← NEW

  const fixedSticky      = document.querySelector('.fixed-sticky');
  const aboutSection     = document.getElementById('about'); // ← NEW
  const cartDrawer       = document.querySelector('#cart-drawer');
  const cartCloseBtn     = document.querySelector('#cart-close');
  const cartPreview      = document.querySelector('.cart-preview');
  const cartPreviewCount = cartPreview.querySelector('span');

  /* ───────────────────────────────
     GENERIC HELPERS
  ─────────────────────────────── */
  const formatPrice = (cents) => (cents / 100).toFixed(2) + ' €';

  const hideMetaData = (hide) => {                  // ← NEW helper
    document.querySelectorAll('.meta-data').forEach(el =>
      el.classList.toggle('is-hidden', hide)
    );
    fixedSticky.classList.toggle('is-hidden', hide);
  };

  const updateCartPreview = (count) => {
    const value = Number(count) || 0;
    cartPreviewCount.textContent   = value;
    cartPreviewCount.dataset.count = value;
  };

  const setButtonState = (btn, available) => {
    if (!btn) return;
    btn.disabled = !available;
    btn.textContent = available ? 'Add to cart' : 'Sold out';
    btn.classList.toggle('sold-out', !available);
  };

  const updateURLHash = (id) => {
    if (!id) return;
    if (history.replaceState) {
      history.replaceState(null, '', `#${id}`);
    } else {
      location.hash = `#${id}`; // fallback
    }
  };

  /* ───────────────────────────────
     SIZE-OPTION FILTERING
  ─────────────────────────────── */
  function updateSizeOptions(sizeSelect, model, variants) {
    if (!sizeSelect) return;

    let firstEnabled = null;

    Array.from(sizeSelect.options).forEach((opt) => {
      const size = opt.value;
      const matching = variants.find(
        (v) => v.option1 === model &&
               v.option2 === size &&
               v.available !== false &&
               v.inventory_quantity !== 0
      );
      const enable = Boolean(matching);
      opt.disabled = !enable;
      opt.hidden   = !enable;
      if (enable && !firstEnabled) firstEnabled = size;
    });

    if (sizeSelect.options[sizeSelect.selectedIndex]?.disabled) {
      sizeSelect.value = firstEnabled || '';
    }
    sizeSelect.disabled = !firstEnabled;
  }

  const addVariantToCart = (id, qty = 1) => {
    if (!id) {
      console.warn('[PDP] Tried to add undefined variant to cart');
      return;
    }
    fetch('/cart/add.js', {
      method : 'POST',
      headers: { 'Content-Type':'application/json', 'Accept':'application/json' },
      body   : JSON.stringify({ id, quantity: qty }),
    }).then(() => {
      fetchCart(false);
      cartPreview.classList.add('item-added');
      setTimeout(() => cartPreview.classList.remove('item-added'), 600);
    });
  };

  /* ───────────────────────────────
     STICKY CLONE (one per viewport)
  ─────────────────────────────── */
  function cloneMetaData(section) {
    const meta = section.querySelector('.meta-data');
    if (!meta) return;

    fixedSticky.innerHTML = '';
    activeVariantId = null;

    const clone = meta.cloneNode(true);
    fixedSticky.appendChild(clone);
    bindStickyEvents(clone, section);
  }

  function bindStickyEvents(clone, originalSection) {
    /* Sticky nodes */
    const modelSticky  = clone.querySelector('.model-selector');
    const sizeSticky   = clone.querySelector('.size-selector');
    const idSticky     = clone.querySelector('.variant-id-input');
    const priceSticky  = clone.querySelector('.variant-price');
    const formSticky   = clone.querySelector('form');
    const atcSticky    = formSticky?.querySelector('[type="submit"], .add-to-cart');

    /* Original nodes */
    const modelOrig  = originalSection.querySelector('.model-selector');
    const sizeOrig   = originalSection.querySelector('.size-selector');
    const idOrig     = originalSection.querySelector('.variant-id-input');
    const priceOrig  = originalSection.querySelector('.variant-price');
    const imgOrig    = originalSection.querySelector('.variant-img');
    const formOrig   = originalSection.querySelector('form');
    const atcOrig    = formOrig?.querySelector('[type="submit"], .add-to-cart');

    const variants = JSON.parse(originalSection.dataset.variants.replaceAll('&quot;', '"'));

    const mirrorValue = (src, dst) => {
      if (dst && dst.value !== src.value) {
        dst.value = src.value;
        dst.dispatchEvent(new Event('input',  { bubbles:true }));
        dst.dispatchEvent(new Event('change', { bubbles:true }));
      }
    };

    function renderVariant(variant, propagate = false) {
      if (!variant) {
        setButtonState(atcSticky,false);
        setButtonState(atcOrig,  false);
        return;
      }

      idSticky.value          = variant.id;
      idOrig.value            = variant.id;
      priceSticky.textContent = formatPrice(variant.price);
      priceOrig.textContent   = formatPrice(variant.price);
      if (variant.featured_image?.src) imgOrig.src = variant.featured_image.src;

      activeVariantId             = variant.id;
      atcSticky.dataset.variantId = variant.id;
      atcOrig.dataset.variantId   = variant.id;

      const available = variant.available !== false && variant.inventory_quantity !== 0;
      setButtonState(atcSticky, available);
      setButtonState(atcOrig,   available);

      if (propagate) {
        mirrorValue(modelSticky, modelOrig);
        mirrorValue(sizeSticky,  sizeOrig);
      }
    }

    function updateVariant(propagate = true) {
      const variant = variants.find(
        v => v.option1 === modelSticky.value && v.option2 === sizeSticky.value
      );
      renderVariant(variant, propagate);
    }

    function mirrorFromOriginal() {
      mirrorValue(modelOrig, modelSticky);
      updateSizeOptions(sizeSticky, modelOrig.value, variants);
      mirrorValue(sizeOrig, sizeSticky);
      updateVariant(false);
    }

    modelOrig.addEventListener('change', mirrorFromOriginal);
    sizeOrig .addEventListener('change', mirrorFromOriginal);

    modelSticky.addEventListener('change', () => {
      updateSizeOptions(sizeSticky, modelSticky.value, variants);
      updateVariant(true);
    });
    sizeSticky .addEventListener('change', () => updateVariant(true));

    formSticky?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (atcSticky?.disabled) return;
      addVariantToCart(atcSticky.dataset.variantId || idSticky.value);
    });

    /* Init */
    updateSizeOptions(sizeSticky, modelSticky.value, variants);
    updateVariant(false);
  }

  /* ───────────────────────────────
     VIEWPORT OBSERVERS
  ─────────────────────────────── */

  /* Products observer */
  const prodObserver = new IntersectionObserver((entries) => {
    if (inAbout) return; // ignore when About visible
    entries.forEach((entry) => {
      if (entry.isIntersecting && currentSection !== entry.target) {
        currentSection = entry.target;
        cloneMetaData(entry.target);
        updateURLHash(entry.target.id);
      }
    });
  }, { threshold: 0.6 });

  document
    .querySelectorAll('section[data-product-id]')
    .forEach(sec => prodObserver.observe(sec));

  /* About observer */
  if (aboutSection) {
    const aboutObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !inAbout) {
        inAbout = true;
        hideMetaData(true);
        updateURLHash('about');
      } else if (!entry.isIntersecting && inAbout) {
        inAbout = false;
        hideMetaData(false);
        /* prodObserver will handle hash for products */
      }
    }, { threshold: 0.6 });

    aboutObserver.observe(aboutSection);
  }

  /* ───────────────────────────────
     PER-SECTION VARIANT / ATC LOGIC
  ─────────────────────────────── */
  document.querySelectorAll('section[data-product-id]').forEach((section) => {
    const modelSel = section.querySelector('.model-selector');
    const sizeSel  = section.querySelector('.size-selector');
    const idInput  = section.querySelector('.variant-id-input');
    const priceOut = section.querySelector('.variant-price');
    const img      = section.querySelector('.variant-img');
    const form     = section.querySelector('form');
    const atcBtn   = form?.querySelector('[type="submit"], .add-to-cart');

    const variants = JSON.parse(section.dataset.variants.replaceAll('&quot;', '"'));

    function renderSection(variant) {
      if (!variant) {
        setButtonState(atcBtn,false);
        return;
      }

      idInput.value        = variant.id;
      priceOut.textContent = formatPrice(variant.price);
      if (variant.featured_image?.src) img.src = variant.featured_image.src;

      activeVariantId        = variant.id;
      atcBtn.dataset.variantId = variant.id;

      const available = variant.available !== false && variant.inventory_quantity !== 0;
      setButtonState(atcBtn, available);
    }

    function updateVariant() {
      const variant = variants.find(
        v => v.option1 === modelSel.value && v.option2 === sizeSel.value
      );
      renderSection(variant);
    }

    function handleModelChange() {
      updateSizeOptions(sizeSel, modelSel.value, variants);
      updateVariant();
    }

    modelSel.addEventListener('change', handleModelChange);
    sizeSel .addEventListener('change', updateVariant);

    /* init */
    updateSizeOptions(sizeSel, modelSel.value, variants);
    updateVariant();

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (atcBtn?.disabled) return;
      addVariantToCart(atcBtn.dataset.variantId || idInput.value);
    });
  });

  /* ───────────────────────────────
     CART DRAWER & PREVIEW BUBBLE
  ─────────────────────────────── */
  function renderCartDrawer(cart) {
    const container = cartDrawer.querySelector('.cart-items');
    const totalOut  = cartDrawer.querySelector('.cart-total');

    updateCartPreview(cart.item_count);
    container.innerHTML = '';

    if (cart.items.length === 0) {
      container.innerHTML = '<p class="cart-empty">No items in your cart.</p>';
      totalOut.textContent = formatPrice(0);
      return;
    }

    cart.items.forEach(item => {
      container.insertAdjacentHTML('beforeend', `
        <div class="cart-item">
          <div class="product-image-wrapper">
            <img src="${item.image}" alt="${item.title}" class="cart-thumb" />
          </div>
          <div class="cart-details">
            <div class="cart-title">${item.product_title}</div>
            <span class="var-1">${item.variant_title ?? ''}</span>
            <div class="cart-controls">
              <button class="remove-item" data-line="${item.key}">Remove</button>
            </div>
            <div class="cart-line-price">${formatPrice(item.line_price)}</div>
          </div>
        </div>`);
    });

    totalOut.textContent = formatPrice(cart.total_price);
    cartDrawer.classList.add('open');

    /* remove buttons */
    container.querySelectorAll('.remove-item').forEach(btn => {
      btn.addEventListener('click', () => {
        const lineNode = btn.closest('.cart-item');
        fetch('/cart/change.js', {
          method : 'POST',
          headers: { 'Content-Type':'application/json','Accept':'application/json' },
          body   : JSON.stringify({ id: btn.dataset.line, quantity: 0 }),
        })
          .then(() => fetch('/cart.js'))
          .then(r => r.json())
          .then(updated => {
            lineNode.remove();
            updateCartPreview(updated.item_count);
            totalOut.textContent = formatPrice(updated.total_price);
            if (updated.item_count === 0) {
              container.innerHTML = '<p class="cart-empty">No items in your cart.</p>';
            }
          });
      });
    });
  }

  function fetchCart(openDrawer = true) {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        renderCartDrawer(cart);
        if (!openDrawer) cartDrawer.classList.remove('open');
      });
  }

  fetchCart(false); // initial bubble sync

  /* toggle drawer via bubble */
  cartPreview.addEventListener('click', () => {
    const pageDimmer = document.querySelector('#shopify-section-fev-product-scroll');
    if (cartDrawer.classList.contains('open')) {
      pageDimmer.style.opacity = 1;
      cartDrawer.classList.remove('open');
      cartPreviewCount.textContent = cartPreviewCount.dataset.count || '0';
    } else {
      pageDimmer.style.opacity = 0.2;
      fetchCart(true);
    }
  });

  /* dimmer click closes drawer */
  document.querySelector('#shopify-section-fev-product-scroll')
    ?.addEventListener('click', () => {
      if (cartDrawer.classList.contains('open')) {
        document.querySelector('#shopify-section-fev-product-scroll').style.opacity = 1;
        cartDrawer.classList.remove('open');
        cartPreviewCount.textContent = cartPreviewCount.dataset.count || '0';
      }
    });

  cartCloseBtn?.addEventListener('click', () => cartDrawer.classList.remove('open'));
});